import * as React from 'react';

import { ContentSection, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { SalesDetailLayout } from '../../_layouts/SalesDetailLayout';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import {
  $flushOutboundDetailsAutoSave,
  $outboundDetailsFormById,
  $updateOutboundDetailsField,
  DEFAULT_OUTBOUND_DETAILS_FORM,
  type OutboundDetailsFormState,
} from '~/modules/trade/_state/scaleTicketOutboundInfoForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import { FieldFocusIndicator } from '~/shared/components/FieldFocusIndicator';

type OutboundTicketTabDetailsProps = {
  ticket: LoadRecord;
  disabled?: boolean;
};

export const OutboundTicketTabDetails: React.FC<
  OutboundTicketTabDetailsProps
> = ({ ticket, disabled = false }) => {
  const t = useTranslations();
  const ff = t.scaleTicketPro.formFields;

  const setFocusedField = useSetAtom($focusedField);
  const focusPositions = useAtomValue($remoteFocusPositions);

  const form =
    useAtomValue($outboundDetailsFormById).get(ticket.id) ??
    DEFAULT_OUTBOUND_DETAILS_FORM;
  const updateField = useSetAtom($updateOutboundDetailsField);
  const flushAutoSave = useSetAtom($flushOutboundDetailsAutoSave);

  const handleFieldChange = React.useCallback(
    (field: keyof OutboundDetailsFormState) => (value: string) => {
      updateField({ field, value, ticketId: ticket.id });
    },
    [updateField, ticket.id],
  );

  const handleFieldBlur = React.useCallback(() => {
    flushAutoSave(ticket.id);
  }, [flushAutoSave, ticket.id]);

  return (
    <SalesDetailLayout className="@OutboundTicketTabDetails p-0">
      <ContentSection
        title={ff.generalInfo}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldFocusIndicator
            fieldId="description"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('description')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.description}
                value={form.description ?? ''}
                onChange={handleFieldChange('description')}
                onBlur={handleFieldBlur}
                placeholder={ff.descriptionPlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>
        </div>
      </ContentSection>

      <ContentSection
        title={ff.transportDetails}
        titleClassName="font-bold text-neutral"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldFocusIndicator
            fieldId="truckNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('truckNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.truckNumber}
                value={form.truckNumber ?? ''}
                onChange={handleFieldChange('truckNumber')}
                onBlur={handleFieldBlur}
                placeholder={ff.truckNumberPlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="trailerNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('trailerNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.trailerNumber}
                value={form.trailerNumber ?? ''}
                onChange={handleFieldChange('trailerNumber')}
                onBlur={handleFieldBlur}
                placeholder={ff.trailerNumberPlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="driverName"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('driverName')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.driverName}
                value={form.driverName ?? ''}
                onChange={handleFieldChange('driverName')}
                onBlur={handleFieldBlur}
                placeholder={ff.driverNamePlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="containerNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('containerNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.containerNumber}
                value={form.containerNumber ?? ''}
                onChange={handleFieldChange('containerNumber')}
                onBlur={handleFieldBlur}
                placeholder={ff.containerNumberPlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="sealNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('sealNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.sealNumber}
                value={form.sealNumber ?? ''}
                onChange={handleFieldChange('sealNumber')}
                onBlur={handleFieldBlur}
                placeholder={ff.sealNumberPlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="bolNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('bolNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={ff.bolNumber}
                value={form.bolNumber ?? ''}
                onChange={handleFieldChange('bolNumber')}
                onBlur={handleFieldBlur}
                placeholder={ff.bolNumberPlaceholder}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="deliveryNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('deliveryNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={t.loadsView.form.deliveryNumber}
                value={form.deliveryNumber ?? ''}
                onChange={handleFieldChange('deliveryNumber')}
                onBlur={handleFieldBlur}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>

          <FieldFocusIndicator
            fieldId="releaseNumber"
            focusPositions={focusPositions}
          >
            <div
              onFocus={() => setFocusedField('releaseNumber')}
              onBlur={() => setFocusedField(null)}
            >
              <TextField
                label={t.loadsView.form.releaseNumber}
                value={form.releaseNumber ?? ''}
                onChange={handleFieldChange('releaseNumber')}
                onBlur={handleFieldBlur}
                isDisabled={disabled}
              />
            </div>
          </FieldFocusIndicator>
        </div>
      </ContentSection>
    </SalesDetailLayout>
  );
};
